import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UserCheck, ArrowLeft, Sparkles } from "lucide-react";
import { apiCreditPackages, apiCreateCreditCheckout, apiExpertCredits, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * BuyCredits page — presented to members as "Buy Extra Expert Reviews".
 *
 * The route path (`/buy-credits`) and the underlying Stripe products still
 * use `credits` internally (25 credits = 1 Expert Review), but every
 * member-facing string is framed in *reviews* per the €9.99 plan contract.
 */
export default function BuyCredits() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(null);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pkgs, c] = await Promise.all([apiCreditPackages(), apiExpertCredits()]);
        setPackages(pkgs);
        setBalance(c);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const buy = async (pkgId) => {
    setBuying(pkgId);
    try {
      const { url } = await apiCreateCreditCheckout(pkgId, window.location.origin);
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(null);
    }
  };

  // Best value = lowest €/review across the packs.
  const bestValue = packages.length
    ? packages.reduce((acc, p) => {
        const reviews = p.reviews ?? Math.floor((p.credits || 0) / 25);
        const v = reviews / p.price_eur;
        return !acc || v > acc.v ? { id: p.id, v } : acc;
      }, null)
    : null;

  return (
    <section className="pt-10 pb-20 max-w-5xl mx-auto" data-testid="buy-credits-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="buy-credits-back"
        className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.25em] text-neutral-400 hover:text-white mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> back
      </button>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000] mb-3">
          ── top up ──
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white">Buy extra Expert Reviews</h1>
        <p className="text-sm text-neutral-400 mt-1.5 max-w-2xl">
          Need more than the 2 reviews included with your monthly membership? Top up
          anytime — extra reviews are yours to keep and are used only after your
          monthly allowance for the current cycle.
        </p>

        {balance && (
          <div className="mt-5 inline-flex items-center gap-3 border border-neutral-900 bg-neutral-950 px-4 py-2" data-testid="current-balance">
            <UserCheck className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">this cycle</span>
            <span className="font-mono text-lg text-white">
              {balance.reviews_remaining ?? 0} / {balance.monthly_allowance ?? 2}
            </span>
            {(balance.extra_reviews ?? 0) > 0 && (
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FFB000]">
                +{balance.extra_reviews} extra
              </span>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="credit-packages">
          {packages.map((p) => {
            const isBest = bestValue?.id === p.id;
            const reviews = p.reviews ?? Math.floor((p.credits || 0) / 25);
            const perReview = (p.price_eur / reviews).toFixed(2);
            return (
              <div
                key={p.id}
                className={`relative border bg-neutral-950 p-5 flex flex-col ${isBest ? "border-[#00E5FF]" : "border-neutral-900"}`}
                data-testid={`pkg-${p.id}`}
              >
                {isBest && (
                  <div className="absolute -top-3 left-3 inline-flex items-center gap-1 bg-[#00E5FF] text-black text-[10px] font-mono font-bold uppercase tracking-[0.2em] px-2 py-0.5">
                    <Sparkles className="w-3 h-3" /> best value
                  </div>
                )}
                <div className="font-black text-3xl text-white" data-testid={`pkg-${p.id}-count`}>{reviews}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mt-0.5">
                  extra {reviews === 1 ? "review" : "reviews"}
                </div>
                <div className="mt-4 font-mono text-2xl text-white">€{p.price_eur.toFixed(2)}</div>
                <div className="text-[10px] font-mono text-neutral-600 mt-1">€{perReview} / review</div>
                <button
                  onClick={() => buy(p.id)}
                  disabled={buying !== null}
                  data-testid={`buy-${p.id}`}
                  className="mt-5 inline-flex items-center justify-center gap-1.5 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-neutral-200 transition-colors disabled:opacity-50"
                >
                  {buying === p.id ? "Redirecting…" : "Buy"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">
          payment via stripe · secure checkout · cancel anytime
        </div>
      </motion.div>
    </section>
  );
}
